import type { ReactNode } from "react";
import { ExternalLink, Instagram, Linkedin, Mic, Radio, Youtube } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  CURATED_ARTICLES,
  INSTAGRAM_ACCOUNTS,
  LINKEDIN_RESOURCES,
  OFFICIAL_GMAC,
  PODCASTS,
  REDDIT_COMMUNITIES,
  WEBSITES,
  YOUTUBE_CHANNELS,
} from "@/lib/gmat-resources";
import { cn } from "@/lib/cn";

export function Resources() {
  return (
    <div className="container space-y-10 py-10">
      <header>
        <div className="text-xs uppercase tracking-wider text-muted-foreground">
          Curated directory
        </div>
        <h1 className="mt-1 font-serif text-5xl font-normal tracking-tight md:text-6xl">
          Best GMAT <span className="italic">resources</span>
        </h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          Hand-picked channels, forums, and blogs that high scorers actually use — ranked by
          quality, not just popularity. We link out; nothing is re-hosted.
        </p>
      </header>

      <Tabs defaultValue="youtube" className="w-full">
        <TabsList className="flex h-auto flex-wrap gap-1 bg-secondary/80 p-1">
          <TabsTrigger value="youtube">YouTube</TabsTrigger>
          <TabsTrigger value="websites">Websites</TabsTrigger>
          <TabsTrigger value="blogs">Blogs</TabsTrigger>
          <TabsTrigger value="social">Social</TabsTrigger>
          <TabsTrigger value="podcasts">Podcasts</TabsTrigger>
          <TabsTrigger value="official">Official GMAC</TabsTrigger>
        </TabsList>

        <TabsContent value="youtube" className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Top channels cited by 700+ scorers and the GMAT Club community.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            {YOUTUBE_CHANNELS.map((ch) => (
              <ResourceCard
                key={ch.id}
                title={ch.name}
                subtitle={ch.bestFor}
                body={ch.why}
                href={ch.url}
                icon={<Youtube className="size-4 text-red-500" />}
                tags={ch.tags}
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="websites" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {WEBSITES.map((w) => (
              <ResourceCard
                key={w.id}
                title={w.name}
                subtitle={w.host}
                body={w.description}
                href={w.url}
                tags={w.tags}
              />
            ))}
            {REDDIT_COMMUNITIES.map((r) => (
              <ResourceCard
                key={r.id}
                title={r.name}
                subtitle={r.host}
                body={r.description}
                href={r.url}
                tags={r.tags}
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="blogs" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {CURATED_ARTICLES.map((a) => (
              <a
                key={a.id}
                href={a.url}
                target="_blank"
                rel="noreferrer noopener"
                className={cn(
                  "group rounded-2xl border border-border bg-card p-5 transition-all",
                  "hover:-translate-y-0.5 hover:shadow-lg hover:shadow-foreground/5",
                )}
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground">{a.source}</span>
                  <Badge variant="outline" className="text-[10px]">{a.tag}</Badge>
                </div>
                <h3 className="mt-2 font-serif text-xl leading-snug">{a.title}</h3>
                <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
                  <span>{a.minutes} min read</span>
                  <ExternalLink className="size-3.5 opacity-0 group-hover:opacity-100" />
                </div>
              </a>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="social" className="space-y-8">
          <section>
            <h3 className="mb-3 flex items-center gap-2 font-medium">
              <Instagram className="size-4" /> Instagram
            </h3>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {INSTAGRAM_ACCOUNTS.map((ig) => (
                <SocialCard
                  key={ig.id}
                  handle={ig.handle}
                  followers={ig.followers}
                  desc={ig.description}
                  url={ig.url}
                />
              ))}
            </div>
          </section>
          <section>
            <h3 className="mb-3 flex items-center gap-2 font-medium">
              <Linkedin className="size-4" /> LinkedIn
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              {LINKEDIN_RESOURCES.map((li) => (
                <SocialCard
                  key={li.id}
                  handle={li.handle}
                  followers={li.followers}
                  desc={li.description}
                  url={li.url}
                />
              ))}
            </div>
          </section>
        </TabsContent>

        <TabsContent value="podcasts" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {PODCASTS.map((p) => (
              <ResourceCard
                key={p.id}
                title={p.name}
                subtitle={p.publisher}
                body={p.description}
                href={p.url}
                icon={<Mic className="size-4 text-primary" />}
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="official" className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Non-negotiable for serious prep — official material defines the test.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            {OFFICIAL_GMAC.map((o) => (
              <ResourceCard
                key={o.id}
                title={o.name}
                subtitle={o.host}
                body={o.description}
                href={o.url}
                icon={<Radio className="size-4 text-accent" />}
              />
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <footer className="rounded-2xl border border-border/60 bg-muted/30 px-6 py-4 text-xs text-muted-foreground">
        <strong className="text-foreground">Licensing note:</strong> Videos open on YouTube with
        creator attribution. Articles and forum posts open in a new tab — we do not re-host
        third-party content. Partner with GMAC for official question banks in production.
      </footer>
    </div>
  );
}

function ResourceCard({
  title,
  subtitle,
  body,
  href,
  icon,
  tags,
}: {
  title: string;
  subtitle: string;
  body: string;
  href: string;
  icon?: ReactNode;
  tags?: string[];
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className={cn(
        "group flex flex-col rounded-2xl border border-border bg-card p-5 transition-all",
        "hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-lg hover:shadow-foreground/5",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          {icon}
          <div>
            <div className="font-medium">{title}</div>
            <div className="text-xs text-muted-foreground">{subtitle}</div>
          </div>
        </div>
        <ExternalLink className="size-4 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
      </div>
      <p className="mt-3 flex-1 text-sm text-muted-foreground">{body}</p>
      {tags && tags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {tags.slice(0, 3).map((t) => (
            <Badge key={t} variant="outline" className="text-[10px] capitalize">
              {t}
            </Badge>
          ))}
        </div>
      )}
    </a>
  );
}

function SocialCard({
  handle,
  followers,
  desc,
  url,
}: {
  handle: string;
  followers?: string;
  desc: string;
  url: string;
}) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer noopener"
      className="rounded-xl border border-border bg-card p-4 transition-colors hover:bg-secondary/40"
    >
      <div className="font-medium">{handle}</div>
      {followers && <div className="text-xs text-muted-foreground">{followers}</div>}
      <p className="mt-2 text-sm text-muted-foreground">{desc}</p>
    </a>
  );
}
